import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import Select from 'react-select';
import { useIntl } from 'react-intl';

import { APIgetPageTemplates } from '../../../api/Pages';

import { slugify, slugifyUrl } from '../../../helpers';

import ProcessingSpinner from '../../ProcessingSpinner';
import NewPageTemplateOption from './NewPageTemplateOption';

const NewPageSetupModal = ({
  handleClose,
  initialValues,
  duplicateSourcePageName,
  onConfirm,
  onCancel,
}) => {
  const intl = useIntl();
  const initialTemplateId = typeof initialValues.template === 'object'
    ? initialValues.template?.id
    : initialValues.template;

  const [draft, setDraft] = useState({
    name: initialValues.name || '',
    url: initialValues.url || '',
    visibility_mode: initialValues.visibility_mode || 'login_required',
    template: initialTemplateId || null,
  });
  const [urlEdited, setUrlEdited] = useState(!!initialValues.url);
  const [errors, setErrors] = useState({});
  const [responseErrors, setResponseErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);

  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    if (duplicateSourcePageName) {
      setTemplates([]);
      setSelectedTemplate(null);
      setTemplatesLoading(false);
      setDraft((prev) => ({ ...prev, template: null }));
      return;
    }

    setTemplatesLoading(true);
    APIgetPageTemplates().then((response) => {
      const templatesData = response?.data || [];
      const initialSelectedTemplate = templatesData.find(
        template => (`${template.id}` === `${initialTemplateId}`),
      ) || templatesData[0] || null;

      setTemplates(templatesData);
      setSelectedTemplate(initialSelectedTemplate);
      setDraft((prev) => ({
        ...prev,
        template: initialSelectedTemplate ? initialSelectedTemplate.id : null,
      }));
      setTemplatesLoading(false);
    }).catch(() => {
      setTemplates([]);
      setSelectedTemplate(null);
      setDraft((prev) => ({ ...prev, template: null }));
      setTemplatesLoading(false);
    });
  }, [initialTemplateId, duplicateSourcePageName]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    setDraft((prev) => {
      const updated = { ...prev };

      if (name === 'name') {
        updated.name = value;
        if (!urlEdited) {
          updated.url = slugify(value);
        }
      }

      if (name === 'url') {
        updated.url = slugifyUrl(value);
        setUrlEdited(true);
      }

      if (name === 'visibility_mode') {
        updated.visibility_mode = value;
      }

      return updated;
    });

    setErrors((prev) => ({ ...prev, [name]: false }));
    setResponseErrors((prev) => ({ ...prev, [name]: null, form: null }));
  }, [urlEdited]);

  const handleContinue = useCallback(() => {
    const nextErrors = {
      name: !draft.name,
      url: !draft.url,
    };

    setErrors(nextErrors);
    setResponseErrors({});

    if (nextErrors.name || nextErrors.url) {
      return;
    }

    const result = onConfirm(draft);

    setSubmitting(true);

    result
      .then((confirmResult) => {
        if (confirmResult !== false) {
          handleClose();
        }
      })
      .catch((error) => {
        const backendUrlError = error?.response?.data?.errors?.['page.url']?.[0];

        if (backendUrlError) {
          setErrors((prev) => ({ ...prev, url: true }));
          setResponseErrors({ url: backendUrlError });
          return;
        }

        setResponseErrors({
          form: intl.formatMessage({ defaultMessage: 'Something went wrong! Please try again.' }),
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  }, [draft, onConfirm, handleClose, intl]);

  const handleCancel = useCallback(() => {
    onCancel();
    handleClose();
  }, [onCancel, handleClose]);

  const handleVisibilityChange = useCallback((option) => {
    if (!option) {
      return;
    }

    setDraft((prev) => ({ ...prev, visibility_mode: option.value }));
    setErrors((prev) => ({ ...prev, visibility_mode: false }));
    setResponseErrors((prev) => ({ ...prev, visibility_mode: null, form: null }));
  }, []);

  const visibilityOptions = [
    {
      value: 'login_required',
      label: intl.formatMessage({ defaultMessage: 'Login required' }),
    },
    {
      value: 'public',
      label: intl.formatMessage({ defaultMessage: 'Public' }),
    },
    {
      value: 'homepage',
      label: intl.formatMessage({ defaultMessage: 'Landing page' }),
    },
  ];

  const handleTemplateChange = useCallback((option) => {
    setSelectedTemplate(option);
    setDraft((prev) => ({
      ...prev,
      template: option ? option.id : null,
    }));
    setResponseErrors((prev) => ({ ...prev, form: null }));
  }, []);

  return (
    <>
      <ModalHeader>
        {intl.formatMessage({ defaultMessage: 'Create Content Portal' })}
      </ModalHeader>
      <ModalBody>
        {!!duplicateSourcePageName && (
          <Alert color="warning" size="sm">
            {intl.formatMessage(
              { defaultMessage: 'You are duplicating "{pageName}". Sections and page settings will be copied after you continue.' },
              { pageName: duplicateSourcePageName },
            )}
          </Alert>
        )}
        <FormGroup>
          <Label for="newPageName">
            {intl.formatMessage({ defaultMessage: 'Page name' })}
          </Label>
          <Input
            id="newPageName"
            name="name"
            autoFocus
            bsSize="sm"
            value={draft.name}
            invalid={!!errors.name}
            onChange={handleChange}
          />
        </FormGroup>
        <FormGroup>
          <Label for="newPageUrl">
            {intl.formatMessage({ defaultMessage: 'Page URL' })}
          </Label>
          <Input
            id="newPageUrl"
            name="url"
            bsSize="sm"
            value={draft.url}
            invalid={!!errors.url}
            onChange={handleChange}
          />
          {responseErrors.url && (
            <FormFeedback>
              {intl.formatMessage({ defaultMessage: 'This page URL is already in use by another page' })}
            </FormFeedback>
          )}
        </FormGroup>
        <FormGroup>
          <Label for="newPageVisibilityInput">
            {intl.formatMessage({ defaultMessage: 'Visibility' })}
          </Label>
          <Select
            id="newPageVisibility"
            inputId="newPageVisibilityInput"
            name="visibility_mode"
            className="page-visibility-select"
            classNamePrefix="page-visibility-select"
            options={visibilityOptions}
            value={visibilityOptions.find(option => option.value === draft.visibility_mode)}
            onChange={handleVisibilityChange}
            isSearchable={false}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
          {draft.visibility_mode === 'login_required' && (
            <Alert color="warning" size="sm" className="mt-2 puck-visibility-field-alert">
              {intl.formatMessage({ defaultMessage: 'A workspace account is required to view and access this Content Portal.' })}
            </Alert>
          )}
          {draft.visibility_mode === 'public' && (
            <Alert color="danger" size="sm" className="mt-2">
              {intl.formatMessage({ defaultMessage: 'This page will be publicly available. All files and Collections used will be viewable and downloadable.' })}
            </Alert>
          )}
          {draft.visibility_mode === 'homepage' && (
            <Alert color="warning" size="sm" className="mt-2">
              {intl.formatMessage({ defaultMessage: 'Content Portal is viewable, but a log in is required to view or download content' })}
            </Alert>
          )}
        </FormGroup>
        {!duplicateSourcePageName && (
          <FormGroup>
            <Label for="newPageTemplate">
              {intl.formatMessage({ defaultMessage: 'Template' })}
            </Label>
            <Select
              id="newPageTemplate"
              inputId="newPageTemplateInput"
              name="template"
              className="page-template-select"
              classNamePrefix="page-template-select"
              placeholder={intl.formatMessage({ defaultMessage: 'Select template' })}
              noOptionsMessage={() => (intl.formatMessage({ defaultMessage: 'No templates' }))}
              getOptionValue={option => `${option.id}`}
              getOptionLabel={option => option.name}
              components={{
                Option: NewPageTemplateOption,
              }}
              isDisabled={templatesLoading}
              isLoading={templatesLoading}
              maxMenuHeight={380}
              options={templates}
              onChange={handleTemplateChange}
              value={selectedTemplate}
              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </FormGroup>
        )}
        {responseErrors.form && (
          <Alert color="danger" size="sm" className="mb-0">
            {responseErrors.form}
          </Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleCancel}>
          {intl.formatMessage({ defaultMessage: 'Cancel' })}
        </Button>
        <Button
          color="primary"
          onClick={handleContinue}
          disabled={submitting}
        >
          {submitting && (
            <ProcessingSpinner />
          )}
          {intl.formatMessage({ defaultMessage: 'Continue' })}
        </Button>
      </ModalFooter>
    </>
  );
};

NewPageSetupModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    name: PropTypes.string,
    url: PropTypes.string,
    visibility_mode: PropTypes.oneOf(['login_required', 'public', 'homepage']),
    template: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      }),
    ]),
  }),
  duplicateSourcePageName: PropTypes.string,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
};

NewPageSetupModal.defaultProps = {
  initialValues: {
    name: '',
    url: '',
    visibility_mode: 'login_required',
    template: null,
  },
  duplicateSourcePageName: '',
  onConfirm: () => true,
  onCancel: () => {},
};

export default NewPageSetupModal;
